<template>
  <Component
    :is="block ? PMessage : 'div'"
    class="s-error-message"
    :class="[
      `s-error-message--${color}`,
      { 's-error-message--padded': !block },
    ]"
    :severity="toSeverity(color)"
    :size="toModernSize(size)"
  >
    <i
      v-if="icon"
      class="fa-solid fa-person-dolly-empty s-error-message__icon"
    />
    <strong
      class="s-error-message__title"
      :class="`s-error-message__title--${size}`"
    >
      {{ titleText }}
    </strong>
    <span v-if="size === 'lg' || subtitle" class="s-error-message__subtitle">
      {{ subtitle ?? tr("general.error.subtitle") }}
    </span>
    <SButton
      v-if="retry"
      class="s-error-message__button"
      :color
      @click="emit('retry')"
    >
      {{ tr("general.error.retry") }}
    </SButton>
    <SButton
      v-if="cancel"
      :action-type="retry ? 'secondary' : undefined"
      class="s-error-message__button"
      :color
      @click="emit('cancel')"
    >
      {{ tr("general.cancel") }}
    </SButton>
  </Component>
</template>

<script setup lang="ts">
/**
 * Error message display with icon, title, subtitle, and action buttons.
 * Supports different sizes, colors, and optional retry/cancel actions.
 */
import { computed } from "vue";

import type { Color } from "@solvari/common-fe";

import { SButton } from "@solvari/common-fe";
import { useI18nGeneral } from "@solvari/translations";

import PMessage from "@/atoms/PMessage.vue";
import { toModernSize, toSeverity } from "@/helpers/backwardsCompatibility.ts";

const { tr } = useI18nGeneral();

const props = withDefaults(
  defineProps<{
    block?: boolean;
    cancel?: boolean;
    color?: Extract<Color, "danger" | "gray" | "primary" | "warning">;
    icon?: boolean;
    retry?: boolean;
    size?: "lg" | "sm";
    subtitle?: string;
    title?: string;
  }>(),
  { icon: true, size: "sm", color: "primary" },
);

const emit = defineEmits<{ (event: "cancel"): void; (event: "retry"): void }>();

const titleText = computed(() => {
  if (props.title) {
    return props.title;
  }
  return {
    sm: tr("general.error.small_title"),
    lg: tr("general.error.large_title"),
  }[props.size];
});
</script>

<style lang="postcss">
.s-error-message {
  &--padded {
    @apply flex flex-col items-center p-2 text-center;
  }

  &__icon {
    @apply mb-3 h-12 w-12;
  }

  &__title {
    @apply text-lg font-medium;

    &--sm {
      @apply text-base;
    }
  }

  &__subtitle {
    @apply mt-2;
  }

  &__button {
    @apply mt-3;
  }

  &--primary .s-error-message {
    &__icon {
      @apply text-primary-300;
    }

    &__title,
    &__subtitle {
      @apply text-primary-700;
    }
  }

  &--gray .s-error-message {
    &__icon {
      @apply text-gray-200;
    }

    &__title,
    &__subtitle {
      @apply text-gray-700;
    }
  }

  &--danger .s-error-message {
    &__icon {
      @apply text-danger-400;
    }

    &__title,
    &__subtitle {
      @apply text-danger-700;
    }
  }

  &--warning .s-error-message {
    &__icon {
      @apply text-warning-400;
    }

    &__title,
    &__subtitle {
      @apply text-warning-700;
    }
  }

  .p-message-text {
    @apply flex flex-col items-center justify-center;
  }
}
</style>
